import useEmblaCarousel from 'embla-carousel-react';
import React from 'react';
import { useEffect, useState } from 'react';
import CarouselControls from './CarouselControls';
import Dots from './Dots';
import clsx from 'clsx';
import { Button } from '@/components';
import Link from 'next/link';
import { VEHICLES_FOR_HOMEPAGE_CAROUSELResult } from 'sanity.types';

type CarouselItem = NonNullable<VEHICLES_FOR_HOMEPAGE_CAROUSELResult>[number];

interface ImageCarouselProps {
    children: React.ReactNode;
    className?: string;
    vehicles: CarouselItem[];
}

const Carousel = ({
    children,
    className = '',
    vehicles,
}: ImageCarouselProps) => {
    const [emblaRef, emblaApi] = useEmblaCarousel({ loop: true });

    // need to selectedIndex to allow this component to re-render in react.
    // Since emblaRef is a ref, it won't re-render even if there are internal changes to its state.
    const [selectedIndex, setSelectedIndex] = useState(0);

    // To change the button values, we need to re-render the component
    // We'll store the vehicles
    const [vehiclesArray] = useState<CarouselItem[]>(vehicles);

    useEffect(() => {
        function selectHandler() {
            const index = emblaApi?.selectedScrollSnap();
            setSelectedIndex(index || 0);
        }

        emblaApi?.on('select', selectHandler);
        // cleanup
        return () => {
            emblaApi?.off('select', selectHandler);
        };
    }, [emblaApi]);

    const length = React.Children.count(children);
    const canScrollNext = !!emblaApi?.canScrollNext();
    const canScrollPrev = !!emblaApi?.canScrollPrev();

    const containerClasses = clsx(
        'z-10',
        'mx-auto',
        'bg-white',
        'pb-2',
        'md:px-6',
        'drop-shadow-2xl',
        'w-full',
        'rounded-lg',
        'mb-8',
        className
    );

    const vehicleId = vehiclesArray[selectedIndex]._id;

    const quoteLinkProps = {
        pathname: '/quote',
        query: { vehicleId },
    };

    return (
        <div className={containerClasses} data-testid="image-carousel">
            <CarouselControls
                canScrollNext={canScrollNext}
                canScrollPrev={canScrollPrev}
                onNext={() => emblaApi?.scrollNext()}
                onPrev={() => emblaApi?.scrollPrev()}
            >
                <div className="overflow-hidden h-full w-full" ref={emblaRef}>
                    <div className="flex h-[400px] sm:h-[460px] px-4 overflow:hidden">
                        {children}
                    </div>
                </div>
            </CarouselControls>
            <div className="flex flex-row justify-center md:justify-around px-4 mb-4">
                <Link href={quoteLinkProps} className="my-0">
                    <Button label="Quote / Request booking" />
                </Link>
            </div>
            <Dots itemsLength={length} selectedIndex={selectedIndex} />
        </div>
    );
};

export default Carousel;
